import { useState } from 'react';
import { AlarmClock, AlertTriangle, Check, ChevronDown, Send } from 'lucide-react';
import { Button, Select, toast } from '../../components/ui';
import { cn } from '../../lib/cn';
import { formatMoney } from '../../lib/format';
import {
  CADENCE_OPTIONS,
  HOUR_OPTIONS,
  chaseReasonPhrase,
  STOP_AFTER_OPTIONS,
  WEEKDAY_OPTIONS,
  formatNextRun,
  useChaseReminders,
  useRunChaseNow,
  useUpdateChaseReminders,
} from './chase-reminders';

/**
 * **The line that ended the weekly ritual.**
 *
 * `/invoices` computed the chase queue every morning — "5 to chase today",
 * "$120,985 across 7 late invoices" — and then did nothing with it. Every
 * collection affordance in this domain (the ageing buckets, the cadence, the
 * bulk chase, the reminder log, the hosted pay page) existed to move that
 * money, and all of it terminated in somebody remembering it was Monday.
 *
 * This bar states, in one sentence and with no click at all, that the app is
 * already handling it: *"Overdue invoices are chased automatically Monday
 * 9:00 AM — next Mon, Aug 17. 5 queued · $103,405."*
 *
 * Four deliberate calls, all borrowed from the timesheet reminder that proved
 * the pattern one folder away:
 *
 * - **It is ON by default and needs no setup.** Jesse's rule is that closing a
 *   gap must not add a step, and a chase somebody has to switch on is a step.
 * - **The schedule editor is COLLAPSED, not absent.** The default answer is
 *   right for almost everybody, so the five controls sit behind a disclosure
 *   and cost nothing to ignore.
 * - **"Chase N now" is a way of not waiting for Monday, not a second feature.**
 *   It calls the same service the scheduler calls, which calls the same
 *   `deliverMany` the bulk-chase button calls — one message, one audience, one
 *   log, however it is triggered.
 * - **It says what it cannot do, above the button.** Development mode, an
 *   invoice with nowhere to send it, an invoice the machine has stopped
 *   chasing: all printed, never discovered afterwards.
 */
export function ChaseReminderBar() {
  const chase = useChaseReminders();
  const update = useUpdateChaseReminders();
  const run = useRunChaseNow();
  const [editing, setEditing] = useState(false);

  // A promise the server cannot back must not appear at all: if the overview
  // fails to load, the page keeps its table and says nothing about chasing.
  if (chase.isPending || chase.isError || !chase.data) return null;

  const data = chase.data;
  const { settings, counts, amounts } = data;
  /**
   * The held invoices, by number, so the sentence names the money rather than
   * only counting it — "2 on hold ($36k): #1141, #1053" is auditable against
   * the table; "2 on hold" sends the reader hunting. Capped so a long list
   * cannot push the bar to three lines.
   */
  const heldNames = data.held
    .slice(0, 3)
    .map((h) => `#${h.number}`)
    .join(', ')
    .concat(data.held.length > 3 ? ` +${data.held.length - 3} more` : '');
  const nextRun = formatNextRun(data.nextRunAt, settings.timeZone);
  const filedOnly = data.email.mode !== 'live';
  const canSendNow = counts.sendable > 0 && data.email.mode !== 'off';

  const chaseNow = async () => {
    const result = await run.mutateAsync({});
    /**
     * ⚠ ANOTHER RUN HOLDS THEM, AND THAT IS NOT A FAILURE. The claim is
     * atomic, so a second press in the same second wins nothing — and telling
     * the owner "nothing went out … still queued" would read as "press it
     * again", which is exactly the gesture that used to produce two identical
     * dunning letters to one AP inbox.
     */
    if (result.reasonCode === 'contended') {
      toast.success('Already going out — another run has these. Nothing was sent twice.');
      return;
    }
    if (result.chased === 0) {
      /**
       * ⚠ NOTHING REACHED A CLIENT, AND THE TOAST SAYS SO IN THOSE WORDS.
       * `overview` comes back on the same response, so the reason is the
       * mailer's own — and the invoices are still queued, which is the fact
       * that decides whether anyone has to do anything.
       */
      const why = chaseReasonPhrase(result.reason ?? result.overview?.lastFailureReason);
      toast.error(
        why
          ? `Nothing went out — ${why}. Still queued.`
          : 'Nothing went out — nothing reached a client. Still queued.',
      );
      return;
    }
    if (result.failed > 0) {
      toast.error(
        `${result.failed} of ${result.chased + result.failed} did not reach the client${
          chaseReasonPhrase(result.overview?.lastFailureReason)
            ? ` — ${chaseReasonPhrase(result.overview?.lastFailureReason)}`
            : ''
        }. They stay queued.`,
      );
    }
    toast.success(
      `Chased ${result.chased} ${result.chased === 1 ? 'invoice' : 'invoices'} · ${formatMoney(
        result.amount,
        { whole: true },
      )}${result.messages !== result.chased ? ` in ${result.messages} messages` : ''}${
        result.mode === 'live' ? '' : ' — written to .mail, not transmitted'
      }.`,
    );
  };

  return (
    <div className="rounded-lg border border-grey-200 bg-grey-50/60 px-3 py-2">
      {/* ⚠ The sentence takes the WHOLE row on a phone and the buttons sit under
          it. Sharing one flex line with two buttons at 390px squeezes the
          paragraph to nothing and prints it one word per line — a wrapping
          `flex-1` beside `shrink-0` siblings has no minimum of its own. */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <AlarmClock
          className={cn('h-4 w-4 shrink-0', settings.enabled ? 'text-accent' : 'text-grey-400')}
          aria-hidden
        />
        <p className="w-full min-w-[15rem] flex-1 basis-full text-body-sm text-grey-600 sm:w-auto sm:basis-0">
          {settings.enabled ? (
            <>
              Overdue invoices are chased{' '}
              <span className="font-bold text-secondary">automatically</span>{' '}
              <span className="font-semibold text-secondary">{data.scheduleLabel}</span>
              {nextRun && (
                <>
                  {' — next '}
                  <span className="font-semibold text-secondary">{nextRun}</span>
                </>
              )}
              {', '}
              <span className="text-grey-500">{data.cadenceLabel}.</span>{' '}
              {counts.queued === 0 ? (
                <span className="text-grey-500">Nothing is due a chase right now.</span>
              ) : (
                /* The BREAKDOWN is deliberately not repeated here: the chips
                   two inches above already print "5 to chase today = 3 never
                   chased + 2 due to follow up", and they are clickable. This
                   line adds the one thing they cannot say — what the queue is
                   WORTH — and then gets out of the way of the table. */
                <span className="font-semibold text-secondary">
                  {counts.queued} queued · {formatMoney(amounts.queued, { whole: true })}.
                </span>
              )}
            </>
          ) : (
            <>
              <span className="font-bold text-secondary">Automatic chasing is off.</span>{' '}
              <span className="text-grey-500">
                {counts.queued === 0
                  ? 'Nothing is due a chase right now.'
                  : `${counts.queued} overdue ${
                      counts.queued === 1 ? 'invoice is' : 'invoices are'
                    } waiting for ${formatMoney(amounts.queued, { whole: true })} and nobody will be
                      chased unless you do it.`}
              </span>
            </>
          )}
        </p>

        <div className="flex w-full shrink-0 items-center gap-2 sm:ml-auto sm:w-auto">
          <Button
            variant="ghost"
            size="sm"
            aria-expanded={editing}
            onClick={() => setEditing((v) => !v)}
            title="Change when chases go out, how often they repeat, or switch them off"
          >
            Schedule
            <ChevronDown
              className={cn('h-3.5 w-3.5 transition-transform', editing && 'rotate-180')}
              aria-hidden
            />
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="min-w-0 flex-1 justify-center sm:flex-none"
            disabled={!canSendNow || run.isPending}
            onClick={chaseNow}
            title={
              data.email.mode === 'off'
                ? 'Outbound email is switched off on this server.'
                : counts.sendable === 0
                  ? counts.queued === 0
                    ? 'No overdue invoice is due a chase today.'
                    : counts.inFlight > 0
                      ? 'A chase is going out right now — wait for it to finish.'
                      : counts.chasedToday > 0
                        ? 'Everything due a chase has already been chased today.'
                        : 'Every queued invoice has nowhere to send to.'
                  : counts.failedToday > 0
                    ? `Retry the ${counts.sendable} that are still queued — ${counts.failedToday} failed today`
                    : `Send the ${counts.sendable} chases now instead of waiting for ${data.scheduleLabel}`
            }
          >
            <Send className="h-3.5 w-3.5" aria-hidden />
            {/* ⚠ "All chased today" is a claim about DELIVERY and may only be
                printed when something was delivered. A failed run leaves the
                invoices sendable, so this reads "Retry 5" instead — the same
                button, the same call, an honest label. */}
            {run.isPending
              ? 'Chasing…'
              : counts.queued === 0
                ? 'Nothing to chase'
                : counts.sendable === 0
                  ? counts.inFlight > 0
                    ? 'Sending…'
                    : counts.chasedToday > 0
                      ? 'All chased today'
                      : 'Nowhere to send'
                  : counts.failedToday > 0
                    ? `Retry ${counts.sendable} now`
                    : `Chase ${counts.sendable} now`}
          </Button>
        </div>
      </div>

      {/**
        * ⚠ **A CHASE THAT FAILED IS REPORTED AS A FAILURE, LOUDLY.**
        *
        * This line used to read "5 already chased today" — built from the mere
        * existence of a claim, which is written BEFORE the message goes. So a
        * run whose every send blew up printed that sentence over the same five
        * invoices the chips above called "5 to chase today" and this card
        * priced at $103,405, with the button greyed to "All chased today". Now
        * the two facts are separate: DELIVERED is a chase, an attempt that
        * reached nobody is a failure that names its own reason and stays in the
        * queue, and the button beside it is the retry.
        */}
      {counts.failedToday > 0 && (
        <p className="mt-1.5 flex items-start gap-1.5 text-caption text-status-late">
          <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden />
          <span>
            <span className="font-semibold">{counts.failedToday}</span>{' '}
            {counts.failedToday === 1 ? 'chase' : 'chases'} did not reach the client today (
            {formatMoney(amounts.failedToday, { whole: true })})
            {chaseReasonPhrase(data.lastFailureReason)
              ? ` — ${chaseReasonPhrase(data.lastFailureReason)}`
              : ''}
            . Nothing was sent, so{' '}
            {counts.failedToday === 1 ? 'it is' : 'they are'} still queued
            {counts.exhausted > 0
              ? ` — ${counts.exhausted} used up today's ${data.maxAttemptsPerDay} automatic tries, so retry ${
                  counts.exhausted === 1 ? 'it' : 'them'
                } here.`
              : ' and the next hourly run will try again.'}
          </span>
        </p>
      )}

      {/* What the run will NOT reach, said before it is pressed rather than
          discovered in the results. Each sentence names its own remedy. */}
      {(counts.unreachable > 0 ||
        counts.paused > 0 ||
        counts.held > 0 ||
        counts.inFlight > 0 ||
        counts.chasedToday > 0) && (
        <p className="mt-1.5 text-caption text-grey-500">
          {counts.chasedToday > 0 && (
            <>
              <span className="font-semibold text-secondary">{counts.chasedToday}</span> already
              chased today.{' '}
            </>
          )}
          {counts.inFlight > 0 && (
            <>
              <span className="font-semibold text-secondary">{counts.inFlight}</span>{' '}
              {counts.inFlight === 1 ? 'is' : 'are'} sending right now.{' '}
            </>
          )}
          {counts.unreachable > 0 && (
            <>
              <span className="font-semibold text-status-partial">{counts.unreachable}</span>{' '}
              {counts.unreachable === 1 ? 'has' : 'have'} no billing email on the client, so{' '}
              {counts.unreachable === 1 ? 'it is' : 'they are'} skipped — add one on the client to
              include {counts.unreachable === 1 ? 'it' : 'them'}.{' '}
            </>
          )}
          {counts.paused > 0 && (
            <>
              <span className="font-semibold text-status-partial">{counts.paused}</span> chased{' '}
              {settings.stopAfter}× already ({formatMoney(amounts.paused, { whole: true })}) — the
              machine has stopped and is waiting for a call.{' '}
            </>
          )}
          {/* Held by a PERSON, not by the stopping rule — a different fact with
              a different remedy, so it gets its own sentence rather than being
              folded into "paused". Without it the queue would appear to have
              silently shrunk. */}
          {counts.held > 0 && (
            <>
              <span className="font-semibold text-status-partial">{counts.held}</span> on hold (
              {formatMoney(amounts.held, { whole: true })}) — taken off the queue by hand
              {heldNames ? `: ${heldNames}` : ''}. Resume {counts.held === 1 ? 'it' : 'them'} on the
              invoice.
            </>
          )}
        </p>
      )}

      {/* The honest line about delivery, exactly where the composer puts it:
          ABOVE the button, before anything is pressed. */}
      {filedOnly && (
        <p className="mt-1.5 text-caption text-grey-500">
          {data.email.mode === 'off'
            ? 'Outbound email is switched off on this server — nothing is written or sent.'
            : `Development mode — ${data.email.reason ?? 'nothing is transmitted.'}`}
        </p>
      )}

      {editing && (
        <div className="mt-2.5 flex flex-wrap items-end gap-3 rounded-lg border border-grey-200 bg-white px-3 py-2.5">
          <label className="flex items-center gap-2 pb-1.5 text-body-sm font-semibold text-secondary">
            <input
              type="checkbox"
              checked={settings.enabled}
              onChange={(e) => update.mutate({ enabled: e.target.checked })}
              className="h-4 w-4 cursor-pointer rounded border-grey-300 text-accent focus:ring-accent/30"
            />
            Chase automatically
          </label>
          <Select
            className="w-36"
            label="Day"
            selectSize="sm"
            value={String(settings.weekday)}
            disabled={!settings.enabled}
            options={WEEKDAY_OPTIONS}
            onChange={(e) => update.mutate({ weekday: Number(e.target.value) })}
          />
          <Select
            className="w-32"
            label="Time"
            selectSize="sm"
            value={String(settings.hour)}
            disabled={!settings.enabled}
            options={HOUR_OPTIONS}
            onChange={(e) => update.mutate({ hour: Number(e.target.value) })}
          />
          {/* ⚠ The cadence is NOT only a scheduler setting: it decides which
              invoices are "due to follow up" on this very screen, so changing
              it moves the chips above the table too. That is the point — there
              is one cadence, and it used to be a constant nobody could reach. */}
          <Select
            className="w-40"
            label="Chase again"
            selectSize="sm"
            value={String(settings.cadenceDays)}
            options={CADENCE_OPTIONS}
            onChange={(e) => update.mutate({ cadenceDays: Number(e.target.value) })}
          />
          <Select
            className="w-40"
            label="Give up"
            selectSize="sm"
            value={String(settings.stopAfter)}
            options={STOP_AFTER_OPTIONS}
            onChange={(e) => update.mutate({ stopAfter: Number(e.target.value) })}
          />
          {/* The zone is a FACT about the org, not a sixth control to fill in:
              "9:00 AM" has to mean 9 AM where the team is, and it does — the
              server reads the wall clock in this zone, so the hour does not
              drift an hour twice a year. */}
          <p className="max-w-[22rem] pb-1.5 text-caption text-grey-500">
            {settings.timeZone.replace(/_/g, ' ')} · an invoice that is not yet due is never
            chased, and one client's several overdue invoices arrive as one statement.
          </p>
          {update.isSuccess && !update.isPending && (
            <p className="flex items-center gap-1 pb-1.5 text-caption font-semibold text-status-paid">
              <Check className="h-3.5 w-3.5" aria-hidden />
              Saved
            </p>
          )}
          {update.isError && (
            <p className="pb-1.5 text-caption font-semibold text-status-late">
              {(update.error as Error)?.message ?? 'That change could not be saved.'}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
